import React, { memo, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, DialogTitle, Divider, IconButton, Link, Typography } from '@mui/material';
import TextDetailDialog from './TextDetailDialog';
import { buildActivityHeader, handleValidateCodevalue } from '../Utility/constant';
import { useSelector } from 'react-redux';
import closeIcon from '../Assets/images/close_red.png';

import { stripHtmlAndWhitespace } from '../Utility/textHelper';

const TruncatedTextCell = memo(({
    text,
    maxLength = 50,
    dialogTitle = 'Note Details',
    stripHtml = false,
    activityType = null,
    recipients = null,
    createdAt = null,
    callStatus = null,
    callDuration = null,
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);

    const handleOpenDialog = useCallback((e) => {
        e.stopPropagation();
        setIsDialogOpen(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setIsDialogOpen(false);
    }, []);

    const processedText = useMemo(() => {
        if (!text) return '';
        return stripHtml ? stripHtmlAndWhitespace(text) : text;
    }, [text, stripHtml]);

    // Build the structured header only for new records that have activityType
    const header = useMemo(() => {
        if (!activityType) return null;
        return buildActivityHeader(activityType, recipients, createdAt, callStatus, callDuration);
    }, [activityType, recipients, createdAt, callStatus, callDuration]);

    if (!processedText && !header) {
        return <Typography variant="body2">-</Typography>;
    }

    const shouldShowViewMore = true;
    const isLongText = processedText.length > maxLength;
    const displayText = isLongText ? `${processedText.substring(0, maxLength)}...` : processedText;

    return (
        <>
            <Typography
                variant="body2"
                sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: 1.4,
                }}
            >
                {displayText}
                {shouldShowViewMore && (
                    <>
                        {' '}
                        <Link
                            component="button"
                            variant="body2"
                            onClick={handleOpenDialog}
                            className="view-more-link"
                        >
                            {handleValidateCodevalue(internationalise, 'COMMON_VIEW_MORE')}
                        </Link>
                    </>
                )}
            </Typography>

            {isDialogOpen && header && (
                <Dialog
                    open={isDialogOpen}
                    onClose={handleCloseDialog}
                    maxWidth="md"
                    fullWidth
                    onClick={(e) => e.stopPropagation()}
                >
                    <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" component="span">
                            {handleValidateCodevalue(internationalise, dialogTitle)}
                        </Typography>
                        <IconButton onClick={handleCloseDialog}>
                            <img src={closeIcon} alt="close" style={{ height: '35px' }} />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        {/* Single-line header with bold values */}
                        <Typography variant="body2" color="text.primary" sx={{ mb: 1.5, lineHeight: 1.6, fontWeight: 700 }}>
                            {header.label}
                            
                            {header.recipientStr && (
                                <>
                                    &nbsp;&nbsp;{header.directionLabel}: {header.recipientStr}
                                </>
                            )}

                            {header.timeStr && (
                                <>
                                    ,&nbsp;&nbsp;Time: {header.timeStr}
                                </>
                            )}

                            {header.callStatus && (
                                <>
                                    ,&nbsp;&nbsp;Status: {header.callStatus}
                                </>
                            )}

                            {header.callDurationStr && (
                                <>
                                    ,&nbsp;&nbsp;Duration: {header.callDurationStr}
                                </>
                            )}
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <div
                            style={{ fontSize: '14px', lineHeight: '1.6', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
                            dangerouslySetInnerHTML={{ __html: text || '' }}
                        />
                    </DialogContent>
                </Dialog>
            )}

            {isDialogOpen && !header && (
                <TextDetailDialog
                    open={isDialogOpen}
                    onClose={handleCloseDialog}
                    title={handleValidateCodevalue(internationalise, dialogTitle)}
                    text={text}
                />
            )}
        </>
    );
});

TruncatedTextCell.displayName = 'TruncatedTextCell';

TruncatedTextCell.propTypes = {
    text: PropTypes.string,
    maxLength: PropTypes.number,
    dialogTitle: PropTypes.string,
    stripHtml: PropTypes.bool,
    activityType: PropTypes.string,
    recipients: PropTypes.string,
    createdAt: PropTypes.string,
    callStatus: PropTypes.string,
    callDuration: PropTypes.number,
};

export default TruncatedTextCell;
